'use client'

import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'
import { useState } from 'react'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import {
	Tooltip as RadixTooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { isHtmlString } from '@/lib/html'
import { cn } from '@/lib/styles'

interface Props {
	readonly children: ReactElement
	readonly content: ReactNode | string
	readonly contentProps?: Partial<ComponentPropsWithoutRef<typeof TooltipContent>>
	readonly open?: boolean
	readonly onOpenChange?: (open: boolean) => void
	readonly asChild?: boolean
}

export function Tooltip({
	children,
	content,
	contentProps,
	open: controlledOpen,
	onOpenChange,
	asChild = false,
}: Props) {
	const [isOpen, setIsOpen] = useState(false)
	const isControlled = controlledOpen !== undefined
	const open = isControlled ? controlledOpen : isOpen

	const handleOpenChange = (value: boolean) => {
		if (!isControlled) {
			setIsOpen(value)
		}
		onOpenChange?.(value)
	}

	return (
		<TooltipProvider delayDuration={0}>
			<RadixTooltip open={open}>
				<TooltipTrigger
					asChild={asChild}
					{...(!asChild && { type: 'button' })}
					onClick={() => handleOpenChange(!open)}
					onMouseEnter={() => handleOpenChange(true)}
					onMouseLeave={() => handleOpenChange(false)}
					onTouchStart={() => handleOpenChange(!open)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							handleOpenChange(!open)
						}
					}}
				>
					{children}
				</TooltipTrigger>
				<TooltipContent {...contentProps} className={cn('max-w-[200px]', !content && 'hidden')}>
					{typeof content === 'string' && isHtmlString(content) ? (
						<CkEditorCSRRenderer variant="textOnly" htmlContent={content} />
					) : (
						<span className="inline-block">{content}</span>
					)}
				</TooltipContent>
			</RadixTooltip>
		</TooltipProvider>
	)
}
